import {
  formatPhoneNumber,
  formatPhoneNumberToRequest,
  validateMessages,
  websiteValidationRegex
} from '../../helper/common'
import cn from 'classnames'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { useEffect, useMemo, useState } from 'react'
import { setLoading } from '../../redux/store/common/slice'
import { useDispatch } from 'react-redux'
import { Input } from '../Input'
import { LABELS, PLACEHOLDERS, VALIDATION_MESSAGES } from '../../constants'
import './forms.addBankAccount.scss'
import { fetchStateOptions } from '../../features/PaymentDetails/api'
import { TOption } from '../Select/types'
import { notification } from '../Notification'
import { routes } from '../../router'
import useRouter from '../../hooks/useRouter'
import { useParams } from 'react-router-dom'
import {
  TGpoDetailsForm,
  TGpoDetailsFormErrorType,
  TGpoDetailsProps
} from './forms.gpoDetails.d'
import { Select } from '../Select'
import { MY_COMMUNITY_KEYS } from '../../features/MyCommunity/constants'
import { addNewGpo, updateGpoDetails } from '../../features/MyCommunity/Gpo/api'
import { initialState } from '../../pages/AddGpo/constants'
import { RowLayout } from '../../features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import './forms.gpoDetails.scss'

export const GpoDetails = ({
  form,
  setIsActive,
  callback
}: TGpoDetailsProps) => {
  const { push } = useRouter()
  const dispatch = useDispatch()
  const [stateList, setStateList] = useState<TOption[]>([])
  const [errors, setErrors] = useState<TGpoDetailsFormErrorType>({})
  const uuid = form.getFieldValue('uuid')

  const phoneNumber = Form.useWatch('phone_number', form)
  const address = Form.useWatch('address', form)

  const { id } = useParams()

  const getStateList = async () => {
    await fetchStateOptions().then((res) => {
      setStateList(
        res.data.results.map((option) => ({
          label: option.id,
          value: option.id
        }))
      )
    })
  }

  const getSelectorsData = async () => {
    dispatch(setLoading(true))
    try {
      getStateList()
    } finally {
      dispatch(setLoading(false))
    }
  }

  useEffect(() => {
    getSelectorsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFinish = (formData: TGpoDetailsForm) => {
    setIsActive(false)
    const modifiedData = {
      name: formData.name,
      phone_number: formatPhoneNumberToRequest(formData.phone_number),
      website: formData.website,
      address: Object.values(formData.address).some(Boolean)
        ? formData.address
        : null
    }
    dispatch(setLoading(true))
    if (id === 'create') {
      addNewGpo(modifiedData)
        .then(() => {
          notification.success({
            message: VALIDATION_MESSAGES.GPO_CREATED
          })
          push(routes.myCommunity + '/' + MY_COMMUNITY_KEYS.gpo)
        })
        .catch((e) => {
          setIsActive(true)
          if (e.data.details) {
            notification.error({ message: e?.data.details })
          }
          setErrors(e.data)
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    } else {
      updateGpoDetails(uuid, modifiedData)
        .then(() => {
          notification.success({
            message: VALIDATION_MESSAGES.GPO_UPDATED
          })
          if (!!callback) callback()
        })
        .catch((e) => {
          setIsActive(true)
          if (e.data.details) {
            notification.error({ message: e?.data.details })
          }
          setErrors(e.data)
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    }
  }

  const isNotEmptyAddress = useMemo(() => {
    if (!address) return false
    else {
      return !!Object.values(address)?.find((item) => !!item)
    }
  }, [address])

  useEffect(() => {
    if (!isNotEmptyAddress) {
      form.setFields(
        ['street', 'city', 'state', 'zip_code'].map((field) => ({
          name: ['address', field],
          errors: []
        }))
      )
    }
  }, [form, isNotEmptyAddress])

  return (
    <CustomForm<TGpoDetailsForm>
      form={form}
      onFinish={handleFinish}
      initialValues={initialState}
      validateMessages={validateMessages}
      onFieldsChange={() => {
        setErrors({})
        setIsActive(true)
      }}
      id="add-bank-account-form"
      className={cn('add-bank-account-form full-width-form')}
    >
      <RowLayout title="Business information">
        <div className="mt-12">
          <div className="row">
            <Input
              propsItem={{
                name: 'name',
                label: LABELS.GPO_NAME,
                help: errors.name && errors.name[0],
                validateStatus: errors.name && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsInput={{
                maxLength: 100,
                showCount: true
              }}
            />
          </div>
          <div className="row gap-24">
            <Input
              propsInput={{ placeholder: PLACEHOLDERS.WEBSITE }}
              propsItem={{
                name: 'website',
                label: LABELS.WEBSITE,
                help: errors.website && errors.website[0],
                validateStatus: errors.website && 'error',
                rules: [
                  {
                    pattern: websiteValidationRegex,
                    validateTrigger: ['onSubmit']
                  }
                ]
              }}
            />
            <Input.Phone
              propsItem={{
                name: 'phone_number',
                label: LABELS.PHONE_NUMBER,
                help: errors.phone_number && errors.phone_number[0],
                validateStatus: errors.phone_number && 'error',
                rules: [
                  {
                    pattern: /^\d{10,15}$/,
                    validateTrigger:
                      phoneNumber?.length === 10 ? ['onSubmit'] : ['onBlur']
                  }
                ]
              }}
              propsInputNumber={{
                controls: false,
                formatter: formatPhoneNumber,
                prefix: phoneNumber ? '+1' : ''
              }}
            />
          </div>
        </div>
      </RowLayout>
      <div className="separator mt-32 mb-24 row full-width" />
      <RowLayout title="Address">
        <div className="mt-12 address-wrapper">
          <div className="row">
            <Input
              propsItem={{
                name: ['address', 'street'],
                label: LABELS.STREET,
                help: errors?.address?.street?.[0],
                validateStatus: errors?.address?.street && 'error',
                rules: [
                  {
                    required: isNotEmptyAddress
                  }
                ]
              }}
              propsInput={{
                maxLength: 100
              }}
            />
          </div>
          <div className="row address-row gap-24">
            <div className="city-input">
              <Input
                propsItem={{
                  name: ['address', 'city'],
                  label: LABELS.CITY,
                  help: errors?.address?.city?.[0],
                  validateStatus: errors?.address?.city && 'error',
                  rules: [
                    {
                      required: isNotEmptyAddress
                    }
                  ]
                }}
              />
            </div>
            <div className="short-field">
              <Select
                options={stateList}
                propsItem={{
                  label: LABELS.STATE,
                  name: ['address', 'state'],
                  help: errors?.address?.state && errors?.address?.state[0],
                  validateStatus: errors?.address?.state && 'error',
                  rules: [
                    {
                      required: isNotEmptyAddress
                    }
                  ]
                }}
                propsSelect={{
                  placeholder: PLACEHOLDERS.PLEASE_SELECT,
                  allowClear: true
                }}
              />
            </div>
            <div className="short-field">
              <Input
                className="column-half"
                propsItem={{
                  name: ['address', 'zip_code'],
                  label: LABELS.ZIP_CODE,
                  help: errors?.address?.zip_code?.[0],
                  validateStatus: errors?.address?.zip_code && 'error',
                  rules: [
                    {
                      required: isNotEmptyAddress
                    }
                  ]
                }}
              />
            </div>
          </div>
        </div>
      </RowLayout>
    </CustomForm>
  )
}
